/**
 * Size Advisor - Artwork Size Calculator
 * Premium, accessible modal component for product pages
 */

class SizeAdvisor {
  constructor(container) {
    this.container = container;
    this.modal = container.querySelector('[data-size-advisor-modal]');
    this.trigger = container.querySelector('[data-size-advisor-trigger]');
    
    // Move modal to body to avoid transform/overflow issues with fixed positioning
    if (this.modal && this.modal.parentNode !== document.body) {
      document.body.appendChild(this.modal);
    }
    
    this.closeBtn = this.modal ? this.modal.querySelector('[data-size-advisor-close]') : null;
    this.form = this.modal ? this.modal.querySelector('[data-size-advisor-form]') : null;
    this.wallInput = this.modal ? this.modal.querySelector('[data-size-advisor-wall]') : null;
    this.sofaInput = this.modal ? this.modal.querySelector('[data-size-advisor-sofa]') : null;
    this.calculateBtn = this.modal ? this.modal.querySelector('[data-size-advisor-calculate]') : null;
    this.resultsContainer = this.modal ? this.modal.querySelector('[data-size-advisor-results]') : null;
    this.resultsList = this.modal ? this.modal.querySelector('[data-size-advisor-results-list]') : null;
    this.whatsappLink = this.modal ? this.modal.querySelector('[data-size-advisor-whatsapp]') : null;
    
    // Step navigation
    this.step1 = this.modal ? this.modal.querySelector('[data-step="1"]') : null;
    this.step2 = this.modal ? this.modal.querySelector('[data-step="2"]') : null;
    this.backBtn = this.modal ? this.modal.querySelector('[data-size-advisor-back]') : null;
    this.summaryContainer = this.modal ? this.modal.querySelector('[data-size-advisor-summary]') : null;
    
    // Get variant data from data attribute
    this.variants = [];
    this.productTitle = '';
    this.whatsappNumber = '31203086015';
    this.setCount = 1; // Default: single piece
    
    // Default translations (English fallback)
    this.translations = {
      noResults: "We couldn't find a perfect match for your space. Send us a photo on WhatsApp and we'll help you personally!",
      setOf: "Set of",
      sizePerPiece: "size per piece, price for complete set",
      bestFit: "Best fit",
      bestFitDesc: "We think this will look great",
      goSmaller: "Go smaller",
      goSmallerDesc: "For a subtle, elegant touch",
      goBigger: "Go bigger",
      goBiggerDesc: "Make a bold statement",
      ourPick: "Our pick",
      from: "from",
      soldOut: "Sold out",
      select: "Select",
      widthHeight: "W × H"
    };
    
    try {
      const dataEl = container.querySelector('[data-size-advisor-data]');
      if (dataEl) {
        const data = JSON.parse(dataEl.textContent);
        this.variants = data.variants || [];
        this.productTitle = data.productTitle || '';
        this.moneyFormat = data.moneyFormat || '€{{amount}}';
        
        // Load translations if available
        if (data.translations) {
          this.translations = { ...this.translations, ...data.translations };
        }
        
        // Detect if this is a set (Set of 2, Set of 3, etc.)
        this.setCount = this.detectSetCount(this.productTitle);
      }
    } catch (e) {
      console.error('Size Advisor: Error parsing variant data', e);
    }
    
    // Quick select buttons
    this.quickSelectBtns = this.modal ? this.modal.querySelectorAll('[data-sofa-preset]') : [];
    
    // Unit toggle
    this.unitBtns = this.modal ? this.modal.querySelectorAll('[data-unit]') : [];
    this.unitLabels = this.modal ? this.modal.querySelectorAll('[data-unit-label]') : [];
    this.unitSuffixes = this.modal ? this.modal.querySelectorAll('[data-unit-suffix]') : [];
    this.currentUnit = 'cm'; // default
    
    // Parse and extract sizes from variants
    this.sizeVariants = this.parseVariantSizes();
    
    // Focus trap elements
    this.focusableElements = null;
    this.firstFocusable = null;
    this.lastFocusable = null;
    
    // Previous active element (to restore focus on close)
    this.previousActiveElement = null;
    
    this.bindEvents();
  }
  
  /**
   * Detect if product is a set (Set of 2, Set of 3, etc.)
   */
  detectSetCount(title) {
    const lowerTitle = title.toLowerCase();
    
    // Check for "set of X" pattern
    const setMatch = lowerTitle.match(/set\s*(?:of\s*)?(\d+)/i);
    if (setMatch) {
      return parseInt(setMatch[1], 10);
    }
    
    // Check for "X-piece" or "X piece" pattern
    const pieceMatch = lowerTitle.match(/(\d+)[\s-]*piece/i);
    if (pieceMatch) {
      return parseInt(pieceMatch[1], 10);
    }
    
    // Check for "duo" (2) or "trio" (3)
    if (lowerTitle.includes('duo') || lowerTitle.includes('pair')) {
      return 2;
    }
    if (lowerTitle.includes('trio') || lowerTitle.includes('triptych')) {
      return 3;
    }
    
    return 1; // Single piece
  }
  
  /**
   * Switch between cm and inch
   */
  switchUnit(unit) {
    const oldUnit = this.currentUnit;
    this.currentUnit = unit;
    
    // Update button states
    this.unitBtns.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.unit === unit);
    });
    
    // Update labels and suffixes
    const unitText = unit === 'cm' ? '(cm)' : '(inch)';
    const suffixText = unit === 'cm' ? 'cm' : 'inch';
    
    this.unitLabels.forEach(label => {
      label.textContent = unitText;
    });
    
    this.unitSuffixes.forEach(suffix => {
      suffix.textContent = suffixText;
    });
    
    // Convert existing values
    if (this.wallInput && this.wallInput.value) {
      this.wallInput.value = this.convertValue(parseFloat(this.wallInput.value), oldUnit, unit);
    }
    if (this.sofaInput && this.sofaInput.value) {
      this.sofaInput.value = this.convertValue(parseFloat(this.sofaInput.value), oldUnit, unit);
    }
    
    // Update input constraints
    if (unit === 'inch') {
      if (this.wallInput) {
        this.wallInput.min = '20';
        this.wallInput.max = '400';
        this.wallInput.placeholder = '120';
      }
      if (this.sofaInput) {
        this.sofaInput.min = '20';
        this.sofaInput.max = '200';
        this.sofaInput.placeholder = '80';
      }
    } else {
      if (this.wallInput) {
        this.wallInput.min = '50';
        this.wallInput.max = '1000';
        this.wallInput.placeholder = '300';
      }
      if (this.sofaInput) {
        this.sofaInput.min = '50';
        this.sofaInput.max = '500';
        this.sofaInput.placeholder = '200';
      }
    }
    
    // Clear quick select active states
    this.quickSelectBtns.forEach(b => b.classList.remove('is-active'));
  }
  
  /**
   * Convert value between units
   */
  convertValue(value, fromUnit, toUnit) {
    if (fromUnit === toUnit || isNaN(value)) return Math.round(value);
    
    if (fromUnit === 'cm' && toUnit === 'inch') {
      return Math.round(value / 2.54);
    } else if (fromUnit === 'inch' && toUnit === 'cm') {
      return Math.round(value * 2.54);
    }
    return Math.round(value);
  }

  /**
   * Parse variant titles to extract size dimensions
   * Looks for patterns like "100x70", "100×70", "100 x 70"
   * Only includes variants WITH a frame (filters out "No Frame")
   */
  parseVariantSizes() {
    const sizeVariants = [];
    const seenSizes = new Set(); // Track unique sizes to avoid duplicates
    
    this.variants.forEach(variant => {
      const title = variant.title || '';
      const lowerTitle = title.toLowerCase();
      
      // Skip variants without a frame
      if (lowerTitle.includes('no frame') || lowerTitle.includes('zonder frame') || lowerTitle.includes('geen frame') || lowerTitle.includes('unframed')) {
        return;
      }
      
      // Normalize the title: replace × with x, remove spaces around x
      const normalizedTitle = title
        .replace(/×/g, 'x')
        .replace(/\s*x\s*/gi, 'x')
        .toLowerCase();
      
      // Match pattern: digits x digits (e.g., 100x70)
      const match = normalizedTitle.match(/(\d+)x(\d+)/);
      
      if (match) {
        const width = parseInt(match[1], 10);
        const height = parseInt(match[2], 10);
        const sizeKey = `${width}x${height}`;
        
        // Only add if we haven't seen this size yet (avoid duplicate sizes with different frames)
        if (!seenSizes.has(sizeKey)) {
          seenSizes.add(sizeKey);
          
          sizeVariants.push({
            id: variant.id,
            title: `${width}x${height}`,
            width: width,
            height: height,
            price: variant.price,
            available: variant.available,
            formattedPrice: this.formatMoney(variant.price)
          });
        }
      }
    });
    
    // Sort by width ascending
    sizeVariants.sort((a, b) => a.width - b.width);
    
    return sizeVariants;
  }
  
  /**
   * Format price using Shopify money format
   */
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    const value = (parseInt(cents, 10) / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.round(value))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  }
  
  bindEvents() {
    // Open modal
    if (this.trigger) {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }
    
    // Close modal
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    // Close on backdrop click
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });
    }
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    // Calculate button
    if (this.calculateBtn) {
      this.calculateBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.calculate();
      });
    }
    
    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.calculate();
      });
    }
    
    // Real-time validation
    if (this.wallInput) {
      this.wallInput.addEventListener('input', () => this.validateForm());
    }
    
    // Quick select buttons for sofa presets
    this.quickSelectBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        // Use inch preset if in inch mode
        const preset = this.currentUnit === 'inch' 
          ? btn.dataset.sofaPresetInch 
          : btn.dataset.sofaPreset;
        if (this.sofaInput && preset) {
          this.sofaInput.value = preset;
          // Update active state
          this.quickSelectBtns.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          this.validateForm();
        }
      });
    });
    
    // Clear quick select active state when manually typing
    if (this.sofaInput) {
      this.sofaInput.addEventListener('input', () => {
        this.quickSelectBtns.forEach(b => b.classList.remove('is-active'));
      });
    }
    
    // Unit toggle buttons
    this.unitBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const unit = btn.dataset.unit;
        if (unit && unit !== this.currentUnit) {
          this.switchUnit(unit);
        }
      });
    });
    
    // Result item click - select variant
    if (this.resultsList) {
      this.resultsList.addEventListener('click', (e) => {
        const resultItem = e.target.closest('[data-variant-id]');
        if (resultItem && !resultItem.classList.contains('size-advisor-result--unavailable')) {
          this.selectVariant(resultItem.dataset.variantId);
        }
      });
    }
    
    // Back button - return to step 1
    if (this.backBtn) {
      this.backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.showStep(1);
      });
    }
  }
  
  isOpen() {
    return this.modal && this.modal.hasAttribute('open');
  }
  
  open() {
    if (!this.modal) return;
    
    // Store current focus
    this.previousActiveElement = document.activeElement;
    
    // Open modal
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    
    // Always start at step 1
    this.showStep(1);
    
    // Setup focus trap
    this.setupFocusTrap();
    
    // Focus first input
    setTimeout(() => {
      if (this.wallInput) {
        this.wallInput.focus();
      }
    }, 100);
  }
  
  close() {
    if (!this.modal) return;
    
    this.modal.removeAttribute('open');
    document.body.style.overflow = '';
    
    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
    
    // Reset form and results
    this.resetForm();
  }
  
  setupFocusTrap() {
    const content = this.modal.querySelector('.size-advisor-modal__content');
    if (!content) return;
    
    this.focusableElements = content.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (this.focusableElements.length === 0) return;
    
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    
    // Add keydown listener for tab trapping
    this.modal.addEventListener('keydown', this.handleTabKey.bind(this));
  }
  
  handleTabKey(e) {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }
  
  validateForm() {
    const wallValue = this.wallInput ? this.wallInput.value.trim() : '';
    const isValid = wallValue !== '' && !isNaN(parseFloat(wallValue)) && parseFloat(wallValue) > 0;
    
    if (this.calculateBtn) {
      this.calculateBtn.disabled = !isValid;
    }
    
    return isValid;
  }
  
  resetForm() {
    if (this.form) {
      this.form.reset();
    }
    
    if (this.resultsContainer) {
      this.resultsContainer.classList.remove('is-visible');
    }
    
    if (this.resultsList) {
      this.resultsList.innerHTML = '';
    }
    
    // Reset to step 1
    this.showStep(1);
    
    // Reset unit toggle to cm
    this.switchUnit('cm');
    
    // Clear quick select active states
    this.quickSelectBtns.forEach(b => b.classList.remove('is-active'));
    
    this.validateForm();
  }
  
  calculate() {
    if (!this.validateForm()) return;
    
    let wallWidth = parseFloat(this.wallInput.value);
    let sofaWidth = this.sofaInput && this.sofaInput.value.trim() !== '' 
      ? parseFloat(this.sofaInput.value) 
      : null;
    
    // Convert to cm if using inches (all internal calculations in cm)
    if (this.currentUnit === 'inch') {
      wallWidth = wallWidth * 2.54;
      if (sofaWidth) sofaWidth = sofaWidth * 2.54;
    }
    
    // Calculate recommended total artwork width
    let recommendedTotalWidth;
    
    if (sofaWidth && sofaWidth > 0) {
      // If sofa width is provided: artwork = sofa_width * 0.7 (range 0.65-0.75)
      recommendedTotalWidth = sofaWidth * 0.7;
    } else {
      // If only wall width: artwork = wall_width * 0.6
      recommendedTotalWidth = wallWidth * 0.6;
    }
    
    // For sets: calculate individual piece width
    // Account for gaps between pieces (approx 5-10cm between each)
    let recommendedWidth = recommendedTotalWidth;
    const gapBetweenPieces = 8; // cm gap between pieces
    
    if (this.setCount > 1) {
      // Total width = (piece_width * count) + (gaps * (count - 1))
      // So: piece_width = (total_width - gaps) / count
      const totalGaps = gapBetweenPieces * (this.setCount - 1);
      recommendedWidth = (recommendedTotalWidth - totalGaps) / this.setCount;
    }
    
    // Find closest matching variants
    const recommendations = this.findRecommendations(recommendedWidth);
    
    // Render results
    this.renderResults(recommendations, wallWidth, sofaWidth);
    
    // Update WhatsApp link
    this.updateWhatsAppLink(wallWidth, sofaWidth);
    
    // Update summary and show step 2
    this.updateSummary(wallWidth, sofaWidth);
    this.showStep(2);
  }
  
  /**
   * Show specific step (1 = input, 2 = results)
   */
  showStep(stepNumber) {
    if (stepNumber === 1) {
      if (this.step1) this.step1.hidden = false;
      if (this.step2) this.step2.hidden = true;
      // Focus back on wall input
      setTimeout(() => {
        if (this.wallInput) this.wallInput.focus();
      }, 100);
    } else if (stepNumber === 2) {
      if (this.step1) this.step1.hidden = true;
      if (this.step2) this.step2.hidden = false;
      // Focus on back button for accessibility
      setTimeout(() => {
        if (this.backBtn) this.backBtn.focus();
      }, 100);
    }
  }
  
  /**
   * Update the summary in step 2 header
   */
  updateSummary(wallWidthCm, sofaWidthCm) {
    if (!this.summaryContainer) return;
    
    // Convert back to current unit for display
    let wallDisplay = wallWidthCm;
    let sofaDisplay = sofaWidthCm;
    const unitLabel = this.currentUnit === 'inch' ? 'inch' : 'cm';
    
    if (this.currentUnit === 'inch') {
      wallDisplay = Math.round(wallWidthCm / 2.54);
      if (sofaDisplay) sofaDisplay = Math.round(sofaWidthCm / 2.54);
    }
    
    let html = `
      <span class="size-advisor-summary__item">
        <span class="size-advisor-summary__label">Wall:</span>
        ${Math.round(wallDisplay)} ${unitLabel}
      </span>
    `;
    
    if (sofaDisplay) {
      html += `
        <span class="size-advisor-summary__item">
          <span class="size-advisor-summary__label">Sofa:</span>
          ${Math.round(sofaDisplay)} ${unitLabel}
        </span>
      `;
    }
    
    this.summaryContainer.innerHTML = html;
  }
  
  findRecommendations(targetWidth) {
    if (this.sizeVariants.length === 0) {
      return { recommended: null, balanced: null, statement: null };
    }
    
    // Find the closest variant to target width
    let closestIndex = 0;
    let closestDiff = Math.abs(this.sizeVariants[0].width - targetWidth);
    
    this.sizeVariants.forEach((variant, index) => {
      const diff = Math.abs(variant.width - targetWidth);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = index;
      }
    });
    
    // Get recommendations:
    // - Recommended (Most popular): closest match
    // - Balanced: one step smaller (if available)
    // - Statement: one step larger (if available)
    
    const recommended = this.sizeVariants[closestIndex];
    const balanced = closestIndex > 0 ? this.sizeVariants[closestIndex - 1] : null;
    const statement = closestIndex < this.sizeVariants.length - 1 
      ? this.sizeVariants[closestIndex + 1] 
      : null;
    
    return { recommended, balanced, statement };
  }
  
  renderResults(recommendations, wallWidth, sofaWidth) {
    if (!this.resultsList || !this.resultsContainer) return;
    
    const { recommended, balanced, statement } = recommendations;
    
    // Check if we have any results
    if (!recommended && !balanced && !statement) {
      this.resultsList.innerHTML = `
        <div class="size-advisor-no-results">
          ${this.translations.noResults}
        </div>
      `;
      this.resultsContainer.classList.add('is-visible');
      return;
    }
    
    let html = '';
    
    // Show set indicator if applicable
    if (this.setCount > 1) {
      html += `
        <div class="size-advisor-set-badge">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            ${this.setCount > 2 ? '<rect x="3" y="14" width="7" height="7"></rect>' : ''}
          </svg>
          <span>${this.translations.setOf} ${this.setCount} — ${this.translations.sizePerPiece}</span>
        </div>
      `;
    }
    
    // Best fit - show first
    if (recommended) {
      html += this.renderResultItem(recommended, this.translations.bestFit, this.translations.bestFitDesc, true);
    }
    
    // Go smaller option
    if (balanced) {
      html += this.renderResultItem(balanced, this.translations.goSmaller, this.translations.goSmallerDesc);
    }
    
    // Go bigger option
    if (statement) {
      html += this.renderResultItem(statement, this.translations.goBigger, this.translations.goBiggerDesc);
    }
    
    this.resultsList.innerHTML = html;
    this.resultsContainer.classList.add('is-visible');
  }
  
  renderResultItem(variant, label, description, isRecommended = false) {
    const unavailableClass = !variant.available ? 'size-advisor-result--unavailable' : '';
    const recommendedClass = isRecommended ? 'size-advisor-result--recommended' : '';
    const priceText = variant.available ? `${this.translations.from} ${variant.formattedPrice}` : this.translations.soldOut;
    
    // Format size with W × H labels
    const sizeDisplay = `${variant.width} × ${variant.height} cm <span class="size-advisor-result__dimensions">(${this.translations.widthHeight})</span>`;
    
    return `
      <li>
        <div 
          class="size-advisor-result ${recommendedClass} ${unavailableClass}"
          data-variant-id="${variant.id}"
          role="button"
          tabindex="${variant.available ? '0' : '-1'}"
          aria-label="${this.translations.select} ${variant.width} by ${variant.height} centimeters - ${label}"
        >
          <div class="size-advisor-result__info">
            <span class="size-advisor-result__label">
              ${label}
              ${isRecommended ? `<span class="size-advisor-result__badge">${this.translations.ourPick}</span>` : ''}
            </span>
            <span class="size-advisor-result__size">${sizeDisplay} — ${description}</span>
          </div>
          <span class="size-advisor-result__price">${priceText}</span>
        </div>
      </li>
    `;
  }
  
  updateWhatsAppLink(wallWidth, sofaWidth) {
    if (!this.whatsappLink) return;
    
    // Build message
    let message = `Salam alaykum, can you help me choose a size for ${this.productTitle}? Wall width: ${wallWidth}cm`;
    if (sofaWidth) {
      message += `, sofa width: ${sofaWidth}cm`;
    }
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    const cleanNumber = this.whatsappNumber.replace(/[^0-9]/g, '');
    
    this.whatsappLink.href = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
  }
  
  selectVariant(variantId) {
    // Find the variant input or select on the page and change it
    const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
    const variantSelect = document.querySelector('select[name="id"]');
    
    // Try to find variant picker and trigger change
    const productForm = document.querySelector('[data-type="add-to-cart-form"]');
    
    if (variantInput) {
      variantInput.checked = true;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (variantSelect) {
      variantSelect.value = variantId;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Try to find and click the matching option in variant picker
    const variantOption = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (variantOption) {
      variantOption.click();
    }
    
    // Also try to match by size title
    const selectedVariant = this.variants.find(v => v.id == variantId);
    if (selectedVariant) {
      // Try to click matching size buttons
      const sizeButtons = document.querySelectorAll('[data-option-type] input[type="radio"], [data-picker-field] input[type="radio"]');
      sizeButtons.forEach(btn => {
        const normalizedBtnValue = btn.value.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
        const normalizedVariantTitle = selectedVariant.title.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
        
        if (normalizedBtnValue === normalizedVariantTitle || normalizedVariantTitle.includes(normalizedBtnValue)) {
          btn.checked = true;
          btn.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      
      // Also try select dropdowns
      const sizeSelects = document.querySelectorAll('select[name^="options"]');
      sizeSelects.forEach(select => {
        Array.from(select.options).forEach(option => {
          const normalizedOptionValue = option.value.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
          const normalizedVariantTitle = selectedVariant.title.replace(/×/g, 'x').replace(/\s*x\s*/gi, 'x').toLowerCase();
          
          if (normalizedOptionValue === normalizedVariantTitle || normalizedVariantTitle.includes(normalizedOptionValue)) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }
    
    // Close modal
    this.close();
  }
}

// Initialize all Size Advisor instances when DOM is ready
function initSizeAdvisor() {
  const containers = document.querySelectorAll('[data-size-advisor]');
  containers.forEach(container => {
    new SizeAdvisor(container);
  });
  
  // Add unit switcher and enhance size variant labels
  initSizeUnitSwitcher();
}

/**
 * Initialize unit switcher for size variants
 */
function initSizeUnitSwitcher() {
  // Find size option containers
  const sizeOptions = document.querySelectorAll('.m-product-option');
  
  sizeOptions.forEach(optionContainer => {
    // Check if this is a size option by looking for size patterns in values
    const labels = optionContainer.querySelectorAll('.m-product-option--node__label, option');
    let hasSizeValues = false;
    
    labels.forEach(label => {
      const text = label.textContent.trim();
      if (/^\d+\s*[x×]\s*\d+/.test(text)) {
        hasSizeValues = true;
      }
    });
    
    if (!hasSizeValues) return;
    
    // Check if switcher already exists
    if (optionContainer.querySelector('.size-unit-switcher')) return;
    
    // Find the label container
    const labelContainer = optionContainer.querySelector('.m-product-option--label');
    if (!labelContainer) return;
    
    // Create unit switcher
    const switcher = document.createElement('div');
    switcher.className = 'size-unit-switcher';
    switcher.innerHTML = `
      <button type="button" class="size-unit-btn is-active" data-size-unit="cm">cm</button>
      <button type="button" class="size-unit-btn" data-size-unit="inch">inch</button>
    `;
    
    // Insert after the label
    labelContainer.appendChild(switcher);
    
    // Add inline hint (width × height) after the selected size value
    const selectedLabel = labelContainer.querySelector('.option-label--selected');
    if (selectedLabel && !labelContainer.querySelector('.size-format-hint')) {
      const hint = document.createElement('span');
      hint.className = 'size-format-hint';
      hint.textContent = '(width × height)';
      selectedLabel.insertAdjacentElement('afterend', hint);
    }
    
    // Store original values on the whole option container (including label)
    storeOriginalSizes(optionContainer);
    
    // Handle unit switching
    switcher.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size-unit]');
      if (!btn) return;
      
      const unit = btn.dataset.sizeUnit;
      
      // Update active state
      switcher.querySelectorAll('.size-unit-btn').forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      
      // Convert sizes (pass the whole option container)
      convertSizeLabels(optionContainer, unit);
    });
    
    // Initial enhancement (format nicely in cm)
    convertSizeLabels(optionContainer, 'cm');
  });
}

/**
 * Store original cm values on elements
 */
function storeOriginalSizes(container) {
  const sizePattern = /^(\d+)\s*[x×]\s*(\d+)/i;
  
  // Store on swatch labels, tooltips, options, and custom dropdown options
  const elements = container.querySelectorAll('.m-product-option--node__label, .m-tooltip__content, option, .m-custom-select__option');
  elements.forEach(el => {
    const text = el.textContent.trim();
    const match = text.match(sizePattern);
    if (match && !el.dataset.originalWidth) {
      el.dataset.originalWidth = match[1];
      el.dataset.originalHeight = match[2];
    }
  });
  
  // Also store on the selected label in the header
  const selectedLabel = container.querySelector('.option-label--selected');
  if (selectedLabel) {
    const text = selectedLabel.textContent.trim();
    const match = text.match(sizePattern);
    if (match) {
      selectedLabel.dataset.originalWidth = match[1];
      selectedLabel.dataset.originalHeight = match[2];
    }
  }
  
  // Store on custom select trigger value
  const customSelectValue = container.querySelector('.m-custom-select__value');
  if (customSelectValue) {
    const text = customSelectValue.textContent.trim();
    const match = text.match(sizePattern);
    if (match) {
      customSelectValue.dataset.originalWidth = match[1];
      customSelectValue.dataset.originalHeight = match[2];
    }
  }
}

/**
 * Convert size labels between cm and inch
 */
function convertSizeLabels(container, unit) {
  const elements = container.querySelectorAll('[data-original-width]');
  
  elements.forEach(el => {
    const widthCm = parseInt(el.dataset.originalWidth, 10);
    const heightCm = parseInt(el.dataset.originalHeight, 10);
    
    let width, height, unitLabel;
    
    if (unit === 'inch') {
      width = Math.round(widthCm / 2.54);
      height = Math.round(heightCm / 2.54);
      unitLabel = '"';
    } else {
      width = widthCm;
      height = heightCm;
      unitLabel = '';
    }
    
    // Format based on element type
    const unitSuffix = unit === 'inch' ? '"' : ' Cm';
    
    if (el.tagName === 'OPTION') {
      el.textContent = `${width}×${height}${unitSuffix} (width × height)`;
    } else if (el.classList.contains('m-tooltip__content')) {
      el.textContent = `${width}×${height}${unitSuffix}`;
    } else if (el.classList.contains('option-label--selected')) {
      // Selected value label
      el.textContent = `${width}×${height}${unitSuffix}`;
    } else if (el.classList.contains('m-custom-select__option')) {
      // Custom dropdown option - update the span inside
      const span = el.querySelector('span:first-child');
      if (span) {
        span.textContent = `${width}×${height}${unitSuffix} (width × height)`;
      }
    } else if (el.classList.contains('m-custom-select__value')) {
      // Custom dropdown trigger value
      el.textContent = `${width}×${height}${unitSuffix} (width × height)`;
    } else {
      // Swatch labels - show with unit
      el.textContent = `${width}×${height}${unitSuffix}`;
    }
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeAdvisor);
} else {
  initSizeAdvisor();
}

// Also initialize on Shopify section render (for theme editor)
document.addEventListener('shopify:section:load', initSizeAdvisor);

// Re-init when variants change
document.addEventListener('variant:change', () => setTimeout(initSizeUnitSwitcher, 50));
document.addEventListener('change', (e) => {
  if (e.target.closest('[data-variant-picker]')) {
    // When a size is selected, update the selected label with correct unit
    setTimeout(() => {
      const optionContainer = e.target.closest('.m-product-option');
      if (optionContainer) {
        const switcher = optionContainer.querySelector('.size-unit-switcher');
        const activeBtn = switcher?.querySelector('.size-unit-btn.is-active');
        const currentUnit = activeBtn?.dataset.sizeUnit || 'cm';
        
        // Re-store original values (the selected label changed)
        const selectedLabel = optionContainer.querySelector('.option-label--selected');
        if (selectedLabel) {
          const text = selectedLabel.textContent.trim();
          const match = text.match(/^(\d+)\s*[x×]\s*(\d+)/i);
          if (match) {
            selectedLabel.dataset.originalWidth = match[1];
            selectedLabel.dataset.originalHeight = match[2];
          }
        }
        
        // Re-apply current unit
        convertSizeLabels(optionContainer, currentUnit);
      }
    }, 50);
  }
});
